import { useMemo } from "react";
import { isWithinLastDays } from "../lib/dates";
import { isNewJob, isRecentlyClosedJob } from "../lib/tags";
import type { Job } from "../lib/types";

const RECENT_DAYS = 7;

/** Banner totaling job state changes (new, closed, status moves) from the last 7 days. */
export function ChangeSummary({ jobs }: { jobs: Job[] }) {
  const { newCount, closedCount, moved } = useMemo(() => {
    const newCount = jobs.filter(isNewJob).length;
    const closedCount = jobs.filter(isRecentlyClosedJob).length;
    const counts = new Map<string, number>();
    for (const j of jobs) {
      if (!isWithinLastDays(j.state_changed_at, RECENT_DAYS)) continue;
      counts.set(j.status, (counts.get(j.status) ?? 0) + 1);
    }
    const moved = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return { newCount, closedCount, moved };
  }, [jobs]);

  const movedTotal = moved.reduce((sum, [, n]) => sum + n, 0);
  const hasChanges = newCount > 0 || closedCount > 0 || movedTotal > 0;

  return (
    <div className="change-summary">
      <strong>Last 7 days:</strong>{" "}
      {hasChanges ? (
        <>
          {newCount > 0 ? <span className="change-item">{newCount} new</span> : null}
          {closedCount > 0 ? <span className="change-item">{closedCount} closed</span> : null}
          {movedTotal > 0 ? (
            <span className="change-item">
              {movedTotal} status change{movedTotal === 1 ? "" : "s"}
              {moved.length ? (
                <span className="muted"> ({moved.map(([status, n]) => `${status} ×${n}`).join(", ")})</span>
              ) : null}
            </span>
          ) : null}
        </>
      ) : (
        <span className="muted">No changes</span>
      )}
    </div>
  );
}
